"use client";

import { useCart } from "@/lib/cart-store";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Minus, Plus, Trash2, ShoppingBag, Send } from "lucide-react";
import { toast } from "sonner";

export function CartDrawer() {
  const { items, isOpen, setOpen, updateQuantity, removeItem, clear, totalPrice, totalItems } = useCart();

  const handleCheckout = () => {
    if (items.length === 0) {
      toast.error("Panier vide", { description: "Ajoutez des produits avant de commander." });
      return;
    }
    const total = totalPrice();
    const count = totalItems();
    toast.success("Commande prête !", {
      description: `${count} article(s) — ${total.toLocaleString("fr-FR")} FCFA. Notre équipe vous contacte.`,
    });
    // Optionally redirect to contact
    setTimeout(() => {
      clear();
      setOpen(false);
      document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
    }, 1500);
  };

  const formatPrice = (n: number) => n.toLocaleString("fr-FR");

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col p-0 bg-cream">
        <SheetHeader className="p-6 pb-4 border-b border-gold/30 bg-gradient-to-r from-secondary/40 to-transparent">
          <SheetTitle className="font-display text-2xl font-extrabold flex items-center gap-3">
            <span className="grid place-items-center w-10 h-10 rounded-full bg-gradient-gold shadow-gold">
              <ShoppingBag className="w-5 h-5 text-white" />
            </span>
            <span className="text-gradient-gold">Mon Panier</span>
          </SheetTitle>
          <SheetDescription className="text-muted-foreground">
            {totalItems() > 0
              ? `${totalItems()} article(s) dans votre panier`
              : "Votre panier est vide pour le moment."}
          </SheetDescription>
        </SheetHeader>

        {/* Items */}
        <div className="flex-1 overflow-y-auto scroll-premium p-4 space-y-3">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-20">
              <div className="grid place-items-center w-20 h-20 rounded-full bg-secondary mb-4">
                <ShoppingBag className="w-9 h-9 text-gold-deep" />
              </div>
              <p className="font-display text-lg font-bold text-foreground">
                Panier vide
              </p>
              <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                Découvrez nos gammes de produits transformés et ajoutez vos favoris.
              </p>
              <Button
                onClick={() => setOpen(false)}
                className="mt-5 bg-gradient-gold text-white shadow-gold hover:shadow-gold-lg rounded-full"
              >
                Découvrir la boutique
              </Button>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className="flex gap-3 p-3 rounded-2xl bg-card border border-border hover:border-gold transition-colors"
              >
                <div className="shrink-0 w-20 h-20 rounded-xl overflow-hidden ring-1 ring-gold/20">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gold-deep font-semibold">
                        {item.range}
                      </div>
                      <h4 className="font-display text-sm font-bold text-foreground leading-tight">
                        {item.name}
                      </h4>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      aria-label="Retirer"
                      className="text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-1 bg-secondary/70 rounded-full p-1">
                      <button
                        onClick={() => updateQuantity(item.id, -1)}
                        aria-label="Diminuer"
                        className="grid place-items-center w-7 h-7 rounded-full bg-card hover:bg-gradient-gold hover:text-white text-foreground transition-colors"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-7 text-center text-sm font-bold text-foreground">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, 1)}
                        aria-label="Augmenter"
                        className="grid place-items-center w-7 h-7 rounded-full bg-card hover:bg-gradient-gold hover:text-white text-foreground transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="text-right">
                      <div className="font-display text-sm font-bold text-gradient-gold">
                        {formatPrice(item.price * item.quantity)} FCFA
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {item.price} FCFA / {item.unit}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer / totals */}
        {items.length > 0 && (
          <SheetFooter className="border-t border-gold/30 p-6 pt-4 bg-gradient-to-t from-secondary/40 to-transparent">
            <div className="space-y-3 mb-3">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>Sous-total ({totalItems()} articles)</span>
                <span className="font-medium text-foreground">
                  {formatPrice(totalPrice())} FCFA
                </span>
              </div>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>Livraison</span>
                <span className="font-medium text-leaf">Offerte dès 50 000 FCFA</span>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="font-display text-lg font-bold text-foreground">Total</span>
                <span className="font-display text-2xl font-extrabold text-gradient-gold">
                  {formatPrice(totalPrice())} FCFA
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={clear}
                className="rounded-full border-border hover:bg-secondary"
              >
                Vider
              </Button>
              <Button
                onClick={handleCheckout}
                className="flex-1 bg-gradient-gold text-white shadow-gold hover:shadow-gold-lg rounded-full font-semibold"
              >
                <Send className="w-4 h-4 mr-2" />
                Commander
              </Button>
            </div>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
